import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Search, MessageSquare } from 'lucide-react';
import SEO, { getFAQSchema, getBreadcrumbSchema } from '../components/SEO';
import { Button } from '../components/ui';

const FAQPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const faqCategories = [
    {
      name: 'Getting Started',
      faqs: [
        {
          question: 'What is CalcBuildAI?',
          answer: 'CalcBuildAI is an AI-powered construction material calculator that helps you estimate the exact quantities of materials needed for your building projects. Simply describe your project in plain English, and our AI will calculate materials, costs, and provide a shopping list.',
        },
        {
          question: 'How do I use the calculator?',
          answer: 'Using CalcBuildAI is simple: 1) Go to the homepage and type your project description (e.g., "10x12 concrete patio 4 inches thick"), 2) Click Calculate, 3) Review your detailed material list with quantities and cost estimates. You can ask follow-up questions to refine your calculation.',
        },
        {
          question: 'What materials can CalcBuildAI calculate?',
          answer: 'CalcBuildAI supports six main material categories: Concrete (patios, slabs, foundations), Drywall (walls, ceilings), Paint (interior/exterior), Flooring (hardwood, tile, laminate, vinyl), Lumber (decks, fences, framing), and Roofing (shingles, underlayment). Each has specialized calculations for accurate estimates.',
        },
        {
          question: 'Do I need to create an account?',
          answer: 'No, you can use CalcBuildAI without an account. Free users get 10 calculations per month. Creating a free account lets you save your calculations, continue conversations, and access them from any device.',
        },
      ],
    },
    {
      name: 'Calculations & Accuracy',
      faqs: [
        {
          question: 'How accurate are the calculations?',
          answer: 'Our calculations are based on industry-standard formulas and include appropriate waste factors (typically 10-15%). We achieve 98% accuracy for standard projects. For complex or unusual projects, we recommend consulting with a professional contractor for verification.',
        },
        {
          question: 'What is a waste factor and why is it included?',
          answer: 'A waste factor accounts for material that will be cut, damaged, or otherwise unusable during installation. Industry standards recommend 10-15% extra material depending on the project type. CalcBuildAI automatically includes appropriate waste factors so you don\'t run short on materials.',
        },
        {
          question: 'Are the cost estimates accurate?',
          answer: 'Cost estimates are based on average market prices and are meant to give you a ballpark figure. Actual prices vary by location, supplier, and time of year. We recommend using our estimates for budgeting purposes and getting actual quotes from your local suppliers.',
        },
        {
          question: 'Can I ask follow-up questions?',
          answer: 'Yes! After receiving your initial calculation, you can ask follow-up questions to refine your estimate. For example, you might ask "What if I use 6-inch thick concrete instead?" or "Add a 10-foot section of steps." Follow-up questions don\'t count against your monthly calculation limit.',
        },
      ],
    },
    {
      name: 'Account & Billing',
      faqs: [
        {
          question: 'What counts as a calculation?',
          answer: 'A calculation is counted when you start a new conversation with the AI. Follow-up questions within the same conversation are free and don\'t count as additional calculations. Your calculation count resets on the 1st of each month.',
        },
        {
          question: 'What\'s the difference between Free and Pro?',
          answer: 'Free users get 10 calculations per month, basic cost estimates, and email support. Pro users get unlimited calculations, advanced cost breakdowns, PDF export, no ads, priority support, and material price comparisons. See our pricing page for full details.',
        },
        {
          question: 'How do I upgrade to Pro?',
          answer: 'To upgrade to Pro, go to the Pricing page and click "Upgrade to Pro." You\'ll be taken to our secure checkout powered by Stripe. Pro subscriptions are available monthly ($12/month) or annually ($99/year, save 31%).',
        },
        {
          question: 'Can I cancel my Pro subscription?',
          answer: 'Yes, you can cancel anytime from your Settings page. When you cancel, you\'ll continue to have access to Pro features until the end of your current billing period. We also offer a 30-day money-back guarantee if you\'re not satisfied.',
        },
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit cards (Visa, Mastercard, American Express, Discover) through our secure payment processor, Stripe. We do not store your payment information on our servers.',
        },
      ],
    },
    {
      name: 'Privacy & Security',
      faqs: [
        {
          question: 'Is my data secure?',
          answer: 'Yes, we take security seriously. All data is encrypted in transit using TLS and at rest. We use Supabase for secure data storage with row-level security policies. We never share or sell your personal information.',
        },
        {
          question: 'Can I delete my account and data?',
          answer: 'Yes, you can delete your account and all associated data from the Settings page. Account deletion is permanent and cannot be undone. All your calculations, profile data, and subscription information will be removed.',
        },
        {
          question: 'Do you use my calculations to train AI?',
          answer: 'No, your individual calculations are not used to train our AI models. We may analyze aggregate, anonymized usage patterns to improve the service, but your specific project details remain private.',
        },
      ],
    },
    {
      name: 'Technical Questions',
      faqs: [
        {
          question: 'What browsers are supported?',
          answer: 'CalcBuildAI works on all modern browsers including Chrome, Firefox, Safari, and Edge. We recommend using the latest version of your browser for the best experience.',
        },
        {
          question: 'Does CalcBuildAI work on mobile?',
          answer: 'Yes! CalcBuildAI is fully responsive and works great on smartphones and tablets. You can calculate materials right from the job site.',
        },
        {
          question: 'Can I export my calculations?',
          answer: 'Pro users can export calculations to PDF format. Free users can copy results to their clipboard. We\'re working on additional export formats like CSV and integration with popular project management tools.',
        },
        {
          question: 'Is there an API for developers?',
          answer: 'We\'re currently developing a public API for developers who want to integrate CalcBuildAI into their applications. If you\'re interested, contact us at support@calcbuildai.com to join the waitlist.',
        },
      ],
    },
  ];

  // Flatten FAQs for schema and search
  const allFaqs = faqCategories.flatMap(cat => cat.faqs);

  // Filter FAQs based on search query
  const filteredCategories = searchQuery
    ? faqCategories.map(cat => ({
        ...cat,
        faqs: cat.faqs.filter(
          faq =>
            faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      })).filter(cat => cat.faqs.length > 0)
    : faqCategories;

  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'FAQ', path: '/faq' },
  ];

  // Combine schemas
  const structuredData = [
    getFAQSchema(allFaqs),
    getBreadcrumbSchema(breadcrumbs),
  ];

  return (
    <>
      <SEO
        title="Frequently Asked Questions"
        description="Find answers to common questions about CalcBuildAI, the AI-powered construction material calculator. Learn about calculations, pricing, accounts, and more."
        canonical="/faq"
        structuredData={structuredData[0]}
      />
      
      {/* Additional schema for breadcrumbs */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData[1])}
      </script>

      <div className="min-h-screen bg-gray-50">
        {/* Hero */}
        <section className="bg-gradient-to-b from-blue-50 to-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            {/* Breadcrumb */}
            <nav className="flex justify-center mb-6 text-sm">
              <ol className="flex items-center gap-2">
                <li><Link to="/" className="text-gray-500 hover:text-blue-600">Home</Link></li>
                <li className="text-gray-400">/</li>
                <li className="text-gray-900 font-medium">FAQ</li>
              </ol>
            </nav>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Find answers to common questions about CalcBuildAI
            </p>

            {/* Search */}
            <div className="max-w-md mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search questions..."
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>
          </div>
        </section>

        {/* FAQ Content */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4">
            {filteredCategories.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No questions match your search.</p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-4 text-blue-600 hover:text-blue-700"
                >
                  Clear search
                </button>
              </div>
            ) : (
              <div className="space-y-8">
                {filteredCategories.map((category, catIdx) => (
                  <div key={catIdx}>
                    <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      {category.name}
                      <span className="text-sm font-normal text-gray-500">
                        ({category.faqs.length} question{category.faqs.length !== 1 ? 's' : ''})
                      </span>
                    </h2>
                    <div className="space-y-3">
                      {category.faqs.map((faq, faqIdx) => {
                        const globalIdx = `${catIdx}-${faqIdx}`;
                        const isOpen = openFaq === globalIdx;
                        
                        return (
                          <div
                            key={faqIdx}
                            className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                          >
                            <button
                              onClick={() => setOpenFaq(isOpen ? null : globalIdx)}
                              className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 rounded-xl"
                              aria-expanded={isOpen}
                              aria-controls={`faq-${globalIdx}`}
                            >
                              <span className="font-semibold text-gray-900 pr-4">
                                {faq.question}
                              </span>
                              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                isOpen ? 'bg-brand-100' : 'bg-gray-100'
                              }`}>
                                {isOpen ? (
                                  <ChevronUp className="w-5 h-5 text-brand-700" aria-hidden="true" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-gray-600" aria-hidden="true" />
                                )}
                              </div>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${
                              isOpen ? 'max-h-96' : 'max-h-0'
                            }`}>
                              <div className="px-6 pb-4 text-gray-600 leading-relaxed">
                                {faq.answer}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Still Have Questions CTA */}
        <section className="py-12 bg-white border-t">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="bg-blue-50 rounded-2xl p-8">
              <MessageSquare className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Still Have Questions?
              </h2>
              <p className="text-gray-600 mb-6">
                Can't find what you're looking for? Our support team is here to help.
              </p>
              <Link to="/contact">
                <Button size="lg">
                  Contact Support
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FAQPage;
